import React from 'react';
import { 
    Box, 
    Typography, 
    useTheme, 
    useMediaQuery,
    Stack,
    Paper,
    Divider,
    Avatar
} from '@mui/material';
import { 
    FileText, 
    CheckCircle2, 
    Download, 
    ArrowRight,
    Building2
} from 'lucide-react';

const HeroSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                backgroundColor: '#F8FAFC', // Consistent Fintech Light background
                py: { xs: 8, md: 12 },
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1280px',
                    mx: 'auto',
                    px: { xs: 3, md: 6 },
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'center',
                    gap: { xs: 8, md: 8 },
                }}
            >
                {/* Left Section: Text & Icon */}
                <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                    
                    <Typography
                        variant="h1"
                        sx={{ 
                            color: '#0F172A', // Deep Slate
                            fontSize: { xs: '2.5rem', sm: '3.5rem', lg: '4rem' }, 
                            fontWeight: 800, 
                            lineHeight: 1.1,
                            letterSpacing: '-0.03em',
                            mb: 4
                        }}
                    >
                        Make unlimited <br />
                        <Box component="span" sx={{ color: '#4F46E5' }}>invoices</Box>
                    </Typography>

                    <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={3} 
                        alignItems={{ xs: 'center', md: 'flex-start' }}
                    >                        

                        {/* Description Text */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#475569', 
                                fontSize: '1.125rem',
                                lineHeight: 1.7,
                                maxWidth: '500px',
                            }}
                        >
                            Stay on top of your billing with the Bizstor platform. Create and send invoices instantly from any device. 
                            Monitor payment status in real-time, automate reminders for overdue payments — all in one place.
                        </Typography>
                    </Stack>
                </Box>

                {/* Right Section: Modern CSS Mock UI (Replaces Image) */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: { xs: '450px', md: '550px' }
                }}>
                    
                    {/* Modern Abstract Background Shape */}
                    <Box
                        sx={{
                            position: 'absolute',
                            height: { xs: '300px', sm: '400px' },
                            width: { xs: '300px', sm: '400px' },
                            background: 'linear-gradient(135deg, #E0E7FF 0%, #CCFBF1 100%)', // Indigo to teal gradient
                            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', // Organic blob
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-10deg)',
                            zIndex: 0,
                            opacity: 0.8,
                            filter: 'blur(20px)'
                        }}
                    />

                    {/* Main Mock Invoice Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            maxWidth: '340px',
                            backgroundColor: '#ffffff',
                            borderRadius: '24px',
                            p: 3,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                        }}
                    >
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Invoice
                                </Typography>
                                <Typography sx={{ fontSize: '1rem', color: '#0F172A', fontWeight: 700 }}>
                                    #INV-2026
                                </Typography>
                            </Box>
                            <Box sx={{ backgroundColor: '#F0FDF4', color: '#16A34A', px: 1.5, py: 0.5, borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 }}>
                                Sent
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 3, borderColor: '#F1F5F9' }} />

                        {/* Client Info */}
                        <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                            <Avatar sx={{ bgcolor: '#F1F5F9', color: '#475569', width: 40, height: 40 }}>
                                <Building2 size={20} />
                            </Avatar>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Billed to</Typography>
                                <Typography sx={{ fontSize: '0.9rem', color: '#0F172A', fontWeight: 600 }}>Acme Corporation</Typography>
                            </Box>
                        </Stack>

                        {/* Amount */}
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontSize: '0.875rem', color: '#64748B', mb: 0.5 }}>Total Due</Typography>
                            <Typography sx={{ fontSize: '2.5rem', color: '#0F172A', fontWeight: 800, lineHeight: 1 }}>
                                LKR.4,500<span style={{ fontSize: '1.5rem', color: '#94A3B8' }}>.00</span>
                            </Typography>
                        </Box>

                        {/* Mock Skeleton Rows (Line Items) */}
                        <Stack spacing={1.5} mb={4}>
                            {[1, 2].map((item) => (
                                <Stack key={item} direction="row" justifyContent="space-between" alignItems="center">
                                    <Box sx={{ width: '60%', height: '8px', bgcolor: '#F1F5F9', borderRadius: '4px' }} />
                                    <Box sx={{ width: '20%', height: '8px', bgcolor: '#E2E8F0', borderRadius: '4px' }} />
                                </Stack>
                            ))}
                        </Stack>

                        {/* Mock Button */}
                        <Box sx={{ 
                            width: '100%', 
                            py: 1.5, 
                            bgcolor: '#F8FAFC', 
                            borderRadius: '12px', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            gap: 1,
                            color: '#475569',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            border: '1px dashed #CBD5E1'
                        }}>
                            <Download size={16} /> Download PDF
                        </Box>
                    </Paper>

                    {/* Floating Success Notification Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'absolute',
                            bottom: { xs: '5%', md: '15%' },
                            right: { xs: '0%', sm: '5%', md: '-5%' },
                            zIndex: 2,
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            p: 2,
                            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '240px'
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            bgcolor: '#10B981', 
                            color: 'white', 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%' 
                        }}>
                            <CheckCircle2 size={20} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 700 }}>Invoice send.</Typography>
                            {/**<Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Just now via Stripe</Typography>*/}
                        </Box>
                    </Paper>

                </Box>
            </Box>
        </Box>
    );
};

export default HeroSection;